import {
  type ActionConfig,
  type BreadcrumbItem,
  PageHeader,
} from '@/components/PageHeader';
import { usePersistentState } from '@/utils/hooks';
import DatePicker from 'antd/es/date-picker';
import Input from 'antd/es/input';
import Select from 'antd/es/select';
import { type PropsWithChildren } from 'react';
import { css, styled, useTheme } from 'styled-components';

const { RangePicker } = DatePicker;

interface FilterOption {
  label: string;
  value: string;
}

interface BaseFilter {
  key: string;
  label: string;
  width?: number;
  placeholder?: string;
}

interface SelectFilter extends BaseFilter {
  type: 'select';
  options: FilterOption[];
  onChange?: (value: string) => void;
}

interface DateRangeFilter extends BaseFilter {
  type: 'dateRange';
  onChange?: (dates: [unknown, unknown] | null) => void;
}

type FilterConfig = SelectFilter | DateRangeFilter;

type TablePageProps = PropsWithChildren<{
  title: string;
  description?: string;
  actions?: ActionConfig[];
  showSearch?: boolean;
  searchPlaceholder?: string;
  onSearch?: (value: string) => void;
  filters?: FilterConfig[];
  loading?: boolean;
  showFullScreenToggle?: boolean;
  breadcrumbs?: BreadcrumbItem[];
}>;

const Container = styled.div<{ $fullScreen: boolean }>`
  ${({ theme, $fullScreen }) => css`
    max-width: ${$fullScreen ? '100%' : `${theme.screenXL}px`};
    margin: 0 auto;
    transition: all ${theme.motionDurationMid} ease;
    width: ${$fullScreen ? '100%' : 'auto'};
    box-sizing: border-box;
  `}
`;

const SearchContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.margin}px;
    align-items: center;
    margin-bottom: ${theme.marginLG}px;
  `}
`;

const FilterContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.margin}px;
    margin-bottom: ${theme.marginLG}px;
    align-items: center;
  `}
`;

export const TablePageLayout = ({
  children,
  title,
  description,
  actions,
  showSearch = false,
  searchPlaceholder = 'Search...',
  onSearch,
  filters = [],
  loading = false,
  showFullScreenToggle = true,
  breadcrumbs,
}: TablePageProps) => {
  const theme = useTheme();
  const [fullScreen, setFullScreen] = usePersistentState('fullScreen', false);

  return (
    <Container $fullScreen={fullScreen}>
      <PageHeader
        title={title}
        description={description}
        actions={actions}
        showFullScreenToggle={showFullScreenToggle}
        fullScreen={fullScreen}
        onFullScreenToggle={setFullScreen}
        breadcrumbs={breadcrumbs}
      />

      {(showSearch || filters.length > 0) && (
        <SearchContainer>
          {showSearch && (
            <Input.Search
              placeholder={searchPlaceholder}
              allowClear
              onChange={(e) => onSearch?.(e.target.value)}
              style={{ width: theme.size * 18 }}
              disabled={loading}
            />
          )}
        </SearchContainer>
      )}

      {filters.length > 0 && (
        <FilterContainer>
          {filters.map((filter) => {
            if (filter.type === 'select') {
              return (
                <Select
                  key={filter.key}
                  placeholder={
                    filter.placeholder || `Filter by ${filter.label}`
                  }
                  style={{ width: filter.width || theme.size * 10 }}
                  disabled={loading}
                  allowClear
                  onChange={filter.onChange}
                >
                  {filter.options?.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              );
            }
            if (filter.type === 'dateRange') {
              return (
                <RangePicker
                  key={filter.key}
                  style={{ width: filter.width || theme.size * 15 }}
                  disabled={loading}
                  onChange={filter.onChange}
                />
              );
            }
            return null;
          })}
        </FilterContainer>
      )}

      {children}
    </Container>
  );
};
